import React, { useEffect, useState } from 'react';
import smsgatewayService from '../../../../../services/smsgateway.service';
import { useParams } from 'react-router-dom';
import Swal from '../../../../helpers/swal';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Loading from '../../../../common/Loading';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

function SmsgatewayTab() {
    const [loading, setLoading] = useState(true);
    const [tokens, setTokens] = useState([]);
    const { clinicId } = useParams();
    const [creatingToken, setCreatingToken] = useState(false);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [selectedToken, setSelectedToken] = useState(null);

    useEffect(() => {
        getTokenList();
    }, [clinicId]);

    const getTokenList = () => {
        setLoading(true);
        smsgatewayService
            .getTokenList(clinicId)
            .then((tokens) => {
                setTokens(tokens.data);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error loading tokens',
                    timer: 3000,
                    showConfirmButton: false,
                });
            });
    };

    const revealToken = (token) => {
        setSelectedToken(token);
        setShowTokenModal(true);
    };

    const copyTokenToClipboard = () => {
        if (selectedToken) {
            navigator.clipboard
                .writeText(selectedToken.token)
                .then(() => {
                    Swal.fire({
                        icon: 'success',
                        title: 'Copied!',
                        text: 'Token copied to clipboard',
                        timer: 1500,
                        showConfirmButton: false,
                        position: 'top-end',
                        toast: true,
                    });
                })
                .catch(() => {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to copy token',
                        timer: 3000,
                        showConfirmButton: false,
                    });
                });
        }
    };

    const confirmBlockToken = (tokenId, username) => {
        Swal.fire({
            title: 'Confirm Block Token',
            html: `
                <p class="small">Are you sure you want to block token <strong>${username}</strong>?</p>
                <p class="small">Blocking this token will prevent it from being used to send SMS messages.</p>
                <p class="small">Please type <span class="text-danger fw-bold">"block token"</span> to confirm.</p>
                <input type="text" id="swal-confirm-input" class="swal2-input small" placeholder='Type "block token" here'>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class='bi bi-check'></i> Block Token`,
            cancelButtonText: `<i class='bi bi-x'></i> Cancel`,
            customClass: {
                html: 'small',
                title: 'card-title',
                confirmButton: 'btn btn-danger me-2 btn-sm',
                cancelButton: 'btn btn-secondary me-2 btn-sm',
            },
            preConfirm: () => {
                const inputValue = document.getElementById('swal-confirm-input').value;
                if (inputValue !== 'block token') {
                    Swal.showValidationMessage('Please type "block token" to confirm');
                    return false;
                }
                return true;
            },
        }).then((result) => {
            if (result.isConfirmed) {
                blockToken(tokenId);
            }
        });
    };

    const blockToken = (tokenId) => {
        smsgatewayService
            .blockToken(clinicId, tokenId)
            .then(() => {
                getTokenList();
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Token blocked successfully',
                    timer: 3000,
                    showConfirmButton: false,
                });
            })
            .catch(() => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error blocking token',
                    timer: 3000,
                    showConfirmButton: false,
                });
            });
    };

    const confirmUnblockToken = (tokenId, username) => {
        Swal.fire({
            title: 'Unblock Token',
            html: `Are you sure you want to unblock token <strong>${username}</strong>?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `<i class='bi bi-check'></i> Yes, unblock it!`,
            cancelButtonText: `<i class='bi bi-x'></i> Cancel`,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-success me-2 btn-sm',
                cancelButton: 'btn btn-secondary me-2 btn-sm',
            },
        }).then((result) => {
            if (result.isConfirmed) {
                unblockToken(tokenId);
            }
        });
    };

    const unblockToken = (tokenId) => {
        smsgatewayService
            .unblockToken(clinicId, tokenId)
            .then(() => {
                getTokenList();
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Token unblocked successfully',
                    timer: 3000,
                    showConfirmButton: false,
                });
            })
            .catch(() => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error unblocking token',
                    timer: 3000,
                    showConfirmButton: false,
                });
            });
    };

    const confirmCreateToken = () => {
        Swal.fire({
            title: 'Create New Token',
            text: 'Are you sure you want to create a new SMS gateway token?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `<i class='bi bi-check'></i> Yes, create it!`,
            cancelButtonText: `<i class='bi bi-x'></i> Cancel`,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-success me-2 btn-sm',
                cancelButton: 'btn btn-secondary me-2 btn-sm',
            },
        }).then((result) => {
            if (result.isConfirmed) {
                createToken();
            }
        });
    };

    const createToken = () => {
        setCreatingToken(true);
        smsgatewayService
            .createToken(clinicId)
            .then(() => {
                getTokenList();
                setCreatingToken(false);
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Token created successfully',
                    timer: 3000,
                    showConfirmButton: false,
                });
            })
            .catch(() => {
                setCreatingToken(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error creating token',
                    timer: 3000,
                    showConfirmButton: false,
                });
            });
    };

    const readableDate = (date) => {
        const d = new Date(date);
        return d.toLocaleString();
    };

    const handleCloseTokenModal = () => {
        setShowTokenModal(false);
        setSelectedToken(null);
    };

    const activeCount = tokens.filter((t) => t.isActive && !t.isBlocked).length;
    const blockedCount = tokens.filter((t) => t.isBlocked).length;

    const getStatusBadges = (token) => {
        const badges = [];
        badges.push(
            <span
                key="status"
                className={`clinic-sms-tokens-status-badge ${
                    token.isActive ? 'clinic-sms-tokens-status-badge--active' : 'clinic-sms-tokens-status-badge--inactive'
                }`}
            >
                {token.isActive ? 'Active' : 'Inactive'}
            </span>
        );
        if (token.isBlocked) {
            badges.push(
                <span key="blocked" className="clinic-sms-tokens-status-badge clinic-sms-tokens-status-badge--blocked ms-1">
                    Blocked
                </span>
            );
        }
        return badges;
    };

    return (
        <>
            <div className="notifications-panel">
                <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-chat-text"></i>
                        SMS Gateway Tokens
                        {!loading && (
                            <span className="text-muted small fw-normal">
                                ({tokens.length} token{tokens.length !== 1 ? 's' : ''})
                            </span>
                        )}
                    </span>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={confirmCreateToken}
                        disabled={loading || creatingToken}
                    >
                        {creatingToken ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Creating…
                            </>
                        ) : (
                            <>
                                <i className="bi bi-key me-1"></i>
                                Create Token
                            </>
                        )}
                    </button>
                </div>

                <div className="notifications-panel__body">
                    {loading ? (
                        <Loading />
                    ) : tokens.length === 0 ? (
                        <div className="clinic-sms-tokens-empty">
                            <i className="bi bi-key"></i>
                            No SMS gateway tokens found.
                            <div className="mt-3">
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={confirmCreateToken} disabled={creatingToken}>
                                    <i className="bi bi-plus-circle me-1"></i>
                                    Create First Token
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="clinic-sms-tokens-toolbar">
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="clinic-sms-tokens-stat clinic-sms-tokens-stat--active">Active: {activeCount}</span>
                                    <span className="clinic-sms-tokens-stat clinic-sms-tokens-stat--blocked">Blocked: {blockedCount}</span>
                                    <span className="clinic-sms-tokens-stat clinic-sms-tokens-stat--total">Total: {tokens.length}</span>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-sms-tokens-table">
                                    <thead>
                                        <tr>
                                            <th>
                                                <i className="bi bi-key"></i> Token
                                            </th>
                                            <th>
                                                <i className="bi bi-person-badge"></i> Role
                                            </th>
                                            <th>
                                                <i className="bi bi-calendar-plus"></i> Created
                                            </th>
                                            <th>
                                                <i className="bi bi-activity"></i> Status
                                            </th>
                                            <th className="text-end">
                                                <i className="bi bi-gear"></i> Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tokens.map((token) => (
                                            <tr key={token.id} className="clinic-sms-tokens-row">
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-key text-primary"></i>
                                                        <span className="clinic-sms-tokens-username">{token.username}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="clinic-sms-tokens-role">{token.role || 'N/A'}</span>
                                                </td>
                                                <td className="clinic-sms-tokens-date">{readableDate(token.createdAt)}</td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">{getStatusBadges(token)}</div>
                                                </td>
                                                <td className="text-end">
                                                    <div className="d-flex flex-wrap justify-content-end gap-1">
                                                        <ActionTooltipButton
                                                            tooltip="Reveal token"
                                                            tooltipId={`reveal-token-${token.id}`}
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => revealToken(token)}
                                                        >
                                                            <i className="bi bi-eye"></i>
                                                        </ActionTooltipButton>
                                                        {token.isBlocked ? (
                                                            <ActionTooltipButton
                                                                tooltip="Unblock token"
                                                                tooltipId={`unblock-token-${token.id}`}
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={() => confirmUnblockToken(token.id, token.username)}
                                                            >
                                                                <i className="bi bi-unlock"></i>
                                                            </ActionTooltipButton>
                                                        ) : (
                                                            <ActionTooltipButton
                                                                tooltip="Block token"
                                                                tooltipId={`block-token-${token.id}`}
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => confirmBlockToken(token.id, token.username)}
                                                            >
                                                                <i className="bi bi-lock"></i>
                                                            </ActionTooltipButton>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="notifications-alert-banner mt-3">
                                <i className="bi bi-shield-lock"></i>
                                <span>Keep gateway tokens secure. Block any token that may have been compromised.</span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Modal
                show={showTokenModal}
                onHide={handleCloseTokenModal}
                size="lg"
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header className="border-0 pb-0">
                    <Modal.Title className="clinic-sms-tokens-modal__title">
                        <i className="bi bi-key me-2"></i>
                        Token Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {selectedToken && (
                        <>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <div className="notifications-info-row mb-0">
                                        <span className="notifications-info-label">Token ID</span>
                                        <span className="notifications-info-value font-monospace small">{selectedToken.id}</span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="notifications-info-row mb-0">
                                        <span className="notifications-info-label">Username</span>
                                        <span className="notifications-info-value font-monospace small">{selectedToken.username}</span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="notifications-info-row mb-0">
                                        <span className="notifications-info-label">Role</span>
                                        <span className="notifications-info-value">{selectedToken.role || 'N/A'}</span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="notifications-info-row mb-0">
                                        <span className="notifications-info-label">Status</span>
                                        <div className="notifications-info-value d-flex flex-wrap gap-1">{getStatusBadges(selectedToken)}</div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="notifications-info-row mb-0">
                                        <span className="notifications-info-label">Created</span>
                                        <span className="notifications-info-value">{readableDate(selectedToken.createdAt)}</span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="notifications-info-row mb-0">
                                        <span className="notifications-info-label">Modified</span>
                                        <span className="notifications-info-value">{readableDate(selectedToken.modifiedAt)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="notifications-info-row mb-2">
                                <span className="notifications-info-label">Token</span>
                                <div className="notifications-url-box">
                                    <code className="flex-grow-1">{selectedToken.token}</code>
                                    <button type="button" className="notifications-copy-btn" onClick={copyTokenToClipboard} title="Copy token">
                                        <i className="bi bi-copy"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="notifications-form-hint">
                                <i className="bi bi-info-circle me-1"></i>
                                This token should be kept secure and not shared unnecessarily.
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="outline-secondary" size="sm" onClick={handleCloseTokenModal}>
                        Close
                    </Button>
                    <Button variant="primary" size="sm" onClick={copyTokenToClipboard}>
                        <i className="bi bi-copy me-1"></i>
                        Copy Token
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default SmsgatewayTab;
